import { RefObject, Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import { IModule, Permissions, TUserWithConnect } from '@cloud-wave/neon-common-lib';
import { PAGE_ROUTES } from '@cloud-wave/neon-common-lib/app';
import { SOFTPHONE_PAGE_ROUTES } from '@cloud-wave/neon-common-lib/app';

// components
import { useModulesContext } from 'lib/common/contexts/ModulesContext';

import { useHistory } from 'lib/common/hooks/useHistory';

import AuthorisedRoute from 'lib/common/components/AuthorisedRoute';
import Loader from 'lib/common/components/Loader';

import { SoftphoneLayout } from 'lib/common/layouts';

const EmailView = lazy(() => import('lib/pages/EmailView'));
const Directory = lazy(() => import('lib/pages/softphone/Directory'));
const InteractionHistory = lazy(() => import('lib/pages/softphone/InteractionHistory'));
const Landing = lazy(() => import('lib/pages/softphone/Landing'));
const Task = lazy(() => import('lib/pages/softphone/Task'));
const PageModule = lazy(() => import('lib/common/components/PageModule'));
const NotFound = lazy(() => import('lib/common/components/NotFound'));
const Dialpad = lazy(() => import('lib/common/components/molecules/Dialpad'));
const AgentStats = lazy(() => import('lib/common/components/molecules/AgentStats'));
const Preferences = lazy(() => import('lib/common/components/molecules/Preferences'));
const DesktopOnly = lazy(() => import('lib/common/components/DesktopOnly'));
const RedirectIfSoftphone = lazy(() => import('lib/common/components/atoms/RedirectIfSoftphone'));

interface ISoftphoneLayoutRoute {
  user: TUserWithConnect;
  isReversedLayout: boolean;
  contentRef: RefObject<HTMLDivElement>;
}

export default function SoftphoneRoutes({ user, isReversedLayout, contentRef }: ISoftphoneLayoutRoute) {
  const {
    state: { pageModules },
  } = useModulesContext();
  const { Navigate } = useHistory();

  return (
    <SoftphoneLayout user={user} isReversedLayout={isReversedLayout} contentRef={contentRef}>
      <Suspense fallback={<Loader centered />}>
        <Routes>
          <Route path="/" element={<Navigate to={SOFTPHONE_PAGE_ROUTES.LANDING} />} />
          <Route path={SOFTPHONE_PAGE_ROUTES.LANDING} element={<Landing />} />
          <Route
            path={SOFTPHONE_PAGE_ROUTES.INTERACTION_HISTORY}
            element={
              <AuthorisedRoute permissionType="tenant" permission={Permissions.CALL_HISTORY}>
                <InteractionHistory />
              </AuthorisedRoute>
            }
          />
          <Route
            path={SOFTPHONE_PAGE_ROUTES.AGENT_STATS}
            element={
              <AuthorisedRoute permissionType="tenant" permission={Permissions.MY_QUEUES}>
                <AgentStats />
              </AuthorisedRoute>
            }
          />
          <Route path={SOFTPHONE_PAGE_ROUTES.DIALPAD} element={<Dialpad />} />
          <Route path={SOFTPHONE_PAGE_ROUTES.DIRECTORY} element={<Directory />} />
          <Route path={SOFTPHONE_PAGE_ROUTES.TASK} element={<Task user={user} />} />
          <Route path={SOFTPHONE_PAGE_ROUTES.PREFERENCES} element={<Preferences />} />
          <Route path={PAGE_ROUTES.EMAIL} element={<EmailView />} />
          {pageModules.map((pageModule: IModule, i) => (
            <Route
              key={i}
              path={pageModule.pathName}
              element={<PageModule pageModuleProps={pageModule} user={user} />}
            />
          ))}
          {/* To handle race condition where both routers are being simultaneously rendered causing an infinite loop */}
          <Route path={PAGE_ROUTES.WORKSPACE} element={<RedirectIfSoftphone to="/" />} />
          <Route path={PAGE_ROUTES.CUSTOMER_PROFILES} element={<DesktopOnly />} />
          <Route path={PAGE_ROUTES.SETTINGS} element={<DesktopOnly />} />
          <Route path={PAGE_ROUTES.REPORTS_WALLBOARD} element={<DesktopOnly />} />
          <Route path={PAGE_ROUTES.REPORTS_ANALYTICS} element={<DesktopOnly />} />
          <Route path={PAGE_ROUTES.REPORTS_PERFORMANCE_TODAY} element={<DesktopOnly />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </SoftphoneLayout>
  );
}
